import React, { useState, useCallback, useEffect } from 'react'
import { useSession, useAuthConfig } from '@emerald-works/react-auth'
import { useIdleTimer } from 'react-idle-timer'
import { Auth } from '@aws-amplify/auth'
import { useLocation } from 'react-router-dom'
import { useTenant } from '@emerald-works-nova/auth'
import { SSOSessionExpiredModal } from '../components'
import { SIGN_IN_PROVIDER_TYPE_STORAGE_KEY, SIGN_IN_USERNAME_PASSWORD } from '../pages/sign-in/components/sign-in'
import debugLogger from '../utils/debugLogger'
import { routes } from '../router/routes'

const SSO_INACTIVITY_TIMEOUT = 10 * 60 * 1000 // 10 minutes

const useSSOSessionExpires = () => {
  const { isAuthenticated, user } = useSession()
  const isBrowserUser = !isAuthenticated
  const tenant = useTenant()
  const authConfig = useAuthConfig()
  const location = useLocation()
  const [showSSOModal, setShowSSOModal] = useState(false)
  const [isSigningIn, setIsSigningIn] = useState(false)

  const hasSSOSetup = tenant.loginProviders.filter(lp => lp.providerType === 'SAML' && lp.providerName !== 'internal').length > 0
  const hasIdentitiesAttributes = !!user?.attributes?.identities
  // Since cognito doesn't return a way to detect if the user has authenticated via SSO
  // we will consider a SSO User if the user has identities attributes and their tenant has sso setup
  // For those users we will skip the session inactivity timeout, since the SSO will re authenticate the user
  const loginProviderType = localStorage.getItem(SIGN_IN_PROVIDER_TYPE_STORAGE_KEY)
  const isSSO = hasSSOSetup && hasIdentitiesAttributes && loginProviderType !== SIGN_IN_USERNAME_PASSWORD

  // Check if current session is valid for SSO users
  const checkSSOSessionValidity = useCallback(async () => {
    try {
      await Auth.currentAuthenticatedUser()
      return true
    } catch (error) {
      debugLogger('[SSO SESSION EXPIRES] SSO session validation failed:', error)
      return false
    }
  }, [])

  // Handle SSO sign in
  const handleSSOSignIn = useCallback(async () => {
    setIsSigningIn(true)
    try {
      const redirectToKey = authConfig.afterAuthLocationCookieName
      const redirectToValue = `${location.pathname}${location.search}`
      if (redirectToValue !== routes.signout.path) {
        localStorage.setItem(redirectToKey, redirectToValue)
      }

      await authConfig.onAuthNeededRedirection({
        Auth,
        federatedSignInParams: { customState: redirectToKey },
        location
      })
      // If we reach this point without redirecting, close the modal
      setShowSSOModal(false)
      setIsSigningIn(false)
    } catch (error) {
      console.error('SSO sign in failed:', error)
      setIsSigningIn(false)
      // Don't close the modal on error, let user retry
    }
  }, [authConfig, location])

  // Handle when user becomes active after being idle
  const handleSSOSessionCheck = useCallback(async () => {
    debugLogger('[SSO SESSION EXPIRES] SSO user is active again')
    if (!isSSO || isBrowserUser || window.location.pathname === routes.login.path || window.location.pathname === routes.signout.path) return

    const isSessionValid = await checkSSOSessionValidity()
    debugLogger('[SSO SESSION EXPIRES] SSO session validity:', isSessionValid)
    if (!isSessionValid) {
      debugLogger('[SSO SESSION EXPIRES] SSO session expired, showing modal')
      setShowSSOModal(true)
    }
  }, [isSSO, isBrowserUser, checkSSOSessionValidity])

  useEffect(() => {
    // Checks sso session on route change
    debugLogger('[SSO SESSION EXPIRES] Checking SSO session on route change:', location.pathname)
    handleSSOSessionCheck()
  }, [location, handleSSOSessionCheck])

  // Only set up idle timer for SSO users
  useIdleTimer({
    timeout: SSO_INACTIVITY_TIMEOUT,
    onIdle: () => {
      // For SSO users, we don't log them out on idle, we just wait for them to return
      debugLogger('[SSO SESSION EXPIRES] SSO user is idle')
    },
    onActive: isSSO ? handleSSOSessionCheck : undefined,
    disabled: !isSSO || isBrowserUser || window.location.pathname === routes.login.path || window.location.pathname === routes.signout.path
  })

  // Render the SSO session expired modal
  const modalComponent = showSSOModal
    ? (
      <SSOSessionExpiredModal
        open={showSSOModal}
        onSignIn={handleSSOSignIn}
        isSigningIn={isSigningIn}
        tenantName={tenant?.name || tenant?.clientName}
      />
      )
    : null

  return modalComponent
}

export default useSSOSessionExpires
